import Repository from './Repository';

class DeviceRepository extends Repository {
	/**
	 * Resolves an array of unassigned device objects for the given client.
	 *
	 * @param {int} clientId
	 * @param {int} page The page number to load
	 * @param {string|null} sortBy
	 * @param {string|null} sortDirection
	 * @param {string|null} filterCriteria
	 * @param {string|array} deviceTypes
	 * @param {string} deviceTypeAttributeIds Device Type Attribute IDs to apply to the filter
	 * @returns {Promise<array>}
	 */
	async getUnassigned (clientId, page, sortBy, sortDirection, filterCriteria, deviceTypes, deviceTypeAttributeIds = []) {
		let sort = '';
		if (sortBy && sortDirection) {
			sort = `&sortBy=${sortBy}&sortDir=${sortDirection}`;
		}

		let filter = '';
		if (filterCriteria) {
			filter = `&filter=${filterCriteria}`;
		}

		let typeFilter = '';
		if (deviceTypes !== '*') {
			typeFilter = `&deviceTypes=${deviceTypes.join(',')}`;
		}

		let include = 'include=deviceType';
		let attributeQueryString = '';
		if (deviceTypeAttributeIds && deviceTypeAttributeIds.length) {
			include += ',deviceAttributes';
			attributeQueryString = '&deviceTypeAttributes=' + deviceTypeAttributeIds.join(',');
		}

		return this.getWithToken(`/clients/${clientId}/unassigned-devices?${include}${attributeQueryString}&page=${page}${sort}${filter}${typeFilter}`);
	}

	/**
	 * Resolves an array of device objects for the given client and group, filtered by the given device type ID.
	 *
	 * @param {int} clientId
	 * @param {int} groupId
	 * @param {string|array} deviceTypeId
	 * @param {int} page
	 * @param {string|null} sortBy
	 * @param {string|null} sortDirection
	 * @param {string|null} filterCriteria
	 * @returns {Promise<array>}
	 */
	async getFromGroupWithDeviceType (clientId, groupId, deviceTypeId, page, sortBy, sortDirection, filterCriteria) {
		let sort = '';
		if (sortBy && sortDirection) {
			sort = `&sortBy=${sortBy}&sortDir=${sortDirection}`;
		}

		let filter = '';
		if (filterCriteria) {
			filter = `&filterCriteria=${filterCriteria}`;
		}

		let deviceTypeFilter = '';
		if (deviceTypeId !== '*') {
			deviceTypeFilter = `filter[device_type_id]=${deviceTypeId.join()}`;
		}

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices?${deviceTypeFilter}&page=${page}${filter}${sort}`);
	}

	/**
	 * Assigns the given array of device IDs to the given group.
	 *
	 * @param {array} deviceIds An array of device IDs
	 * @param {int} clientId The client ID
	 * @param {int} id The group ID to assign to
	 * @returns {Promise}
	 */
	async assign (deviceIds, clientId, id) {
		return this.postWithToken(`/clients/${clientId}/groups/${id}/devices`, {
			device_ids: deviceIds
		});
	}

	/**
	 * Unassigns the given array of device IDs from their assigned group.
	 *
	 * @param {array} deviceIds An array of device IDs
	 * @param {int} clientId The client ID
	 * @param {int} id The group ID to assign to
	 */
	async unassign (deviceIds, clientId, id) {
		return this.patchWithToken(`/clients/${clientId}/groups/${id}/devices/unassign`, {
			device_ids: deviceIds
		});
	}

	/**
	 * Retrieves a single device from the given client, group, and device ID.
	 *
	 * @param {int} clientId The client ID
	 * @param {int} groupId The group ID
	 * @param {int} id The device ID
	 * @returns {Promise<object>}
	 */
	async getDevice (clientId, groupId, id) {
		return this.getWithToken(
			`/clients/${clientId}/groups/${groupId}/devices/${id}?include=device-attributes.category,additionalInfo`
		);
	}

	getWattboxLinks (clientId, groupId, id) {
		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices/${id}/wattbox-links`);
	}

	/**
	 * Retrieves an array of monitors from the given client, group, and device ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @param {int} deviceId The device ID to query against
	 * @returns {Promise<array>}
	 */
	async getMonitors (clientId, groupId, deviceId) {
		const include = [
			'threshold-setting',
			'monitor-action-group-template.monitor-action-groups.success-action',
			'monitor-action-group-template.monitor-action-groups.failure-action'
			// 'notifications',
			// 'notifications.monitored',
			// 'audits.monitorAudits',
			// 'audits.monitorAudits.monitored',
			// 'audits'
		].join(',');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices/${deviceId}/monitors?include=-${include}`);
	}

	/**
	 * Retrieves an array of monitor types from the given device ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @param {int} deviceId The device ID to query against
	 */
	async getMonitorTypes (clientId, groupId, deviceId) {
		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices/${deviceId}/monitors/types`);
	}

	/**
	 * Retrieves the given monitor type from the given device ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @param {int} deviceId The device ID to query against
	 * @param {string} monitorType The monitor type key
	 * @param {object} settings The settings to be sent with the query
	 */
	async getMonitorType (clientId, groupId, deviceId, monitorType, settings) {
		const query = Object.keys(settings)
			.filter(key => settings[key] !== '')
			.map(key => key + '=' + settings[key]).join('&');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices/${deviceId}/monitors/types/${monitorType}?${query}`);
	}

	/**
	 * Retrieves an array of inherited monitors from the given client, group, and device ID.
	 *
	 * @param {int} clientId The client ID to query against
	 * @param {int} groupId The group ID to query against
	 * @param {int} deviceId The device ID to query against
	 * @returns {Promise<array>}
	 */
	async getInheritedMonitors (clientId, groupId, deviceId) {
		const include = [
			'threshold-setting',
			'monitor-action-group-template.monitor-action-groups.success-action',
			'monitor-action-group-template.monitor-action-groups.failure-action'
			// 'notifications',
			// 'notifications.monitored',
			// 'audits',
			// 'audits.monitorAudits',
			// 'audits.monitorAudits.monitored'
		].join(',');

		return this.getWithToken(`/clients/${clientId}/groups/${groupId}/devices/${deviceId}/monitors/inherited?include=${include}`);
	}

	/**
	 * retrieves the notifications for a device
	 *
	 * @param clientId
	 * @returns {Promise<Array|Object|string>}
	 */
	async getNotifications (deviceId) {
		return this.getWithToken(`/devices/${deviceId}/notifications`);
	}

	deactivateBulk (deviceList) {
		return this.putWithToken('/devices/deactivate', { devices: deviceList });
	}

	deactivateDevice (id) {
		return this.deleteWithToken(`/devices/${id}/deactivate`);
	}

	activateDevice (id) {
		return this.patchWithToken(`/devices/${id}/activate`);
	}

	refreshDevice (id) {
		return this.putWithToken(`/devices/${id}/refresh`);
	}

	refreshDeviceList (list) {
		return this.putWithToken('/devices/refresh', { device_ids: list });
	}

	rebootDevice (id, outletNumber = null) {
		const data = outletNumber !== null ? { outlet_number: outletNumber } : {};
		return this.putWithToken(`/devices/${id}/reboot`, data);
	}

	getTypes () {
		return this.getWithToken('/devices/types');
	}

	getTypeAttributes (type) {
		return this.getWithToken(`/devices/attributes?type=${type}`);
	}

	search (request) {
		const queryString = Object.keys(request).map(key => key + '=' + request[key]).join('&');

		return this.getWithToken(`/devices/search?${queryString}`);
	}

	getChildrenByType (request) {
		const queryString = Object.keys(request).map(key => {
			if (request[key]) {
				return key + '=' + request[key];
			}

			return key + '=';
		}).join('&');

		return this.getWithToken(`/devices/${request.deviceId}/children?${queryString}`);
	}

	getAttributeType (id) {
		return this.getWithToken(`/device-attributes/${id}/type`);
	}

	/**
	 * get data for a module
	 *
	 * @param {int} id id of the device
	 * @param {string} module slug of the module
	 * @returns {Promise<Array|Object|string>}
	 */
	getModuleData (id, module) {
		return this.getWithToken(`/devices/${id}/${module}`);
	}

	/**
	 * trigger screenshot sync for a single device
	 *
	 * @param {int} id id of the device
	 * @returns {Promise<Array|Object|string>}
	 */
	refreshDeviceScreenshot (id) {
		return this.putWithToken(`/devices/${id}/screenshot/refresh`);
	}

	/**
	 * trigger screenshot sync for a group of devices
	 *
	 * @param {int} id id of the device
	 * @param {string} module slug of the module
	 * @returns {Promise<Array|Object|string>}
	 */
	refreshSelectScreenshots (ids) {
		return this.putWithToken('/devices/screenshot/refresh', { device_ids: ids });
	}

	/**
	 * get screenshot image data using device and screenshot ids
	 *
	 * @param {int} deviceId
	 * @param {int} screenshotId
	 * @returns {Promise<Array|Object|string>}
	 */
	getScreenshotImage (deviceId, screenshotId, size) {
		let path = `/devices/${deviceId}/screenshot/${screenshotId}`;

		if (size) {
			path = `${path}?size=${size}`;
		}
		return this.getWithToken(path);
	}
}

export default new DeviceRepository();
